import { addDays, differenceInCalendarDays, startOfDay } from "date-fns";
import type { Identifier } from "ra-core";

import type { DealMilestone, TeamTask } from "@/components/fretum/types";

import { dayKey, isAllDay, toLocalDate } from "../../mobile/mobileModel";
import { getTaskKind } from "../../tasks/taskKind";
import type { Deal, Task } from "../../types";
import type { HubEvent, Invoice } from "./homeTypes";

/**
 * Pure data shaping of the «Hoy» home (view A): which deals have no next
 * step, the «Necesita tu atención» inbox and the «Por cobrar» buckets. No
 * React, no data provider: every function takes `now` so tests pin the clock.
 */

export type HomeTask = Pick<
  Task,
  "id" | "text" | "due_date" | "done_date" | "contact_id" | "kind" | "type"
>;
export type HomeTeamTask = Pick<
  TeamTask,
  "id" | "title" | "due_date" | "status" | "priority" | "deal_id"
>;
export type HomeMilestone = Pick<
  DealMilestone,
  "id" | "deal_id" | "title" | "due_date" | "status" | "amount"
>;
export type HomeDeal = Pick<
  Deal,
  | "id"
  | "name"
  | "stage"
  | "amount"
  | "contact_ids"
  | "archived_at"
  | "expected_closing_date"
  | "updated_at"
  | "company_id"
>;
export type HomeEvent = Pick<
  HubEvent,
  "id" | "title" | "start_at" | "end_at" | "all_day" | "kind" | "deal_id"
>;
export type HomeInvoice = Pick<
  Invoice,
  | "id"
  | "number"
  | "amount"
  | "tax_rate"
  | "status"
  | "issue_date"
  | "due_date"
  | "paid_at"
  | "company_id"
  | "deal_id"
>;

const amountOf = (value: number | null | undefined): number =>
  Number.isFinite(Number(value)) ? Number(value) : 0;

/** Open = not archived, not lost and not in a won / delivery stage. */
export const isOpenDeal = (
  deal: Pick<HomeDeal, "stage" | "archived_at">,
  closedStages: readonly string[],
): boolean =>
  !deal.archived_at &&
  deal.stage !== "lost" &&
  !closedStages.includes(deal.stage);

/** Amount with VAT: what the client actually pays. */
export const invoiceGross = (
  invoice: Pick<HomeInvoice, "amount" | "tax_rate">,
): number => amountOf(invoice.amount) * (1 + amountOf(invoice.tax_rate) / 100);

/** Issued (or flagged overdue) and not paid yet. */
export const isOutstanding = (invoice: Pick<HomeInvoice, "status">): boolean =>
  invoice.status === "issued" || invoice.status === "overdue";

export interface NoNextStepDeal {
  deal: HomeDeal;
  /** Days since the deal was last touched (`updated_at`). */
  idleDays: number;
  /** The expected closing date is already in the past. */
  closingPassed: boolean;
}

export interface NextStepSources {
  /** Contact tasks of anyone (the link to a deal goes through its contacts). */
  tasks: readonly HomeTask[];
  teamTasks: readonly HomeTeamTask[];
  events: readonly HomeEvent[];
  milestones: readonly HomeMilestone[];
}

/**
 * «Sin próximo paso»: open deals with nothing planned from today on. A deal
 * has a next step when it has, dated today or later, any of:
 * - an open contact task on one of its contacts (tasks have no deal_id),
 * - an open team task linked to it,
 * - a calendar event linked to it,
 * - a milestone of it that is not done.
 * Undated items do not count: a step with no date is not planned.
 * Sorted by amount (what is at stake), then by idle time.
 */
export const dealsWithoutNextStep = (
  deals: readonly HomeDeal[],
  sources: NextStepSources,
  closedStages: readonly string[],
  now: Date,
): NoNextStepDeal[] => {
  const today = dayKey(now);
  const fromToday = (value?: string | null) =>
    value != null && value !== "" && dayKey(value) >= today;

  const plannedContacts = new Set(
    sources.tasks
      .filter((task) => !task.done_date && fromToday(task.due_date))
      .map((task) => String(task.contact_id))
      .filter((id) => id !== "null" && id !== "undefined"),
  );
  const plannedDeals = new Set<string>();
  for (const task of sources.teamTasks) {
    if (
      task.status !== "done" &&
      task.deal_id != null &&
      fromToday(task.due_date)
    )
      plannedDeals.add(String(task.deal_id));
  }
  for (const event of sources.events) {
    if (event.deal_id != null && fromToday(event.end_at ?? event.start_at))
      plannedDeals.add(String(event.deal_id));
  }
  for (const milestone of sources.milestones) {
    if (milestone.status !== "done" && fromToday(milestone.due_date))
      plannedDeals.add(String(milestone.deal_id));
  }

  return deals
    .filter((deal) => isOpenDeal(deal, closedStages))
    .filter(
      (deal) =>
        !plannedDeals.has(String(deal.id)) &&
        !(deal.contact_ids ?? []).some((id) => plannedContacts.has(String(id))),
    )
    .map((deal) => ({
      deal,
      idleDays: deal.updated_at
        ? Math.max(
            0,
            differenceInCalendarDays(now, toLocalDate(deal.updated_at)),
          )
        : 0,
      closingPassed:
        !!deal.expected_closing_date &&
        dayKey(deal.expected_closing_date) < today,
    }))
    .sort(
      (left, right) =>
        amountOf(right.deal.amount) - amountOf(left.deal.amount) ||
        right.idleDays - left.idleDays,
    );
};

export type AttentionGroupKey =
  "overdue" | "today" | "no_next_step" | "collect";
export const ATTENTION_GROUPS: readonly AttentionGroupKey[] = [
  "overdue",
  "today",
  "no_next_step",
  "collect",
];

export type AttentionSource =
  "task" | "team_task" | "milestone" | "event" | "deal" | "invoice";

export interface AttentionItem {
  /** Unique across sources, e.g. "task-12". */
  key: string;
  source: AttentionSource;
  id: Identifier;
  title: string;
  /** The date that puts the item in its group (due, start, closing). */
  date: Date | null;
  /** `date` carries a clock time worth showing. */
  timed: boolean;
  /** Whole days past `date` (overdue items), else 0. */
  daysLate: number;
  amount?: number;
  dealId?: Identifier | null;
  contactId?: Identifier | null;
  companyId?: Identifier | null;
  /** Contact tasks: internal (team) or client task. */
  taskKind?: "internal" | "client";
  taskType?: string;
  priority?: string;
  eventKind?: HubEvent["kind"];
  stage?: string;
  idleDays?: number;
  closingPassed?: boolean;
  /** Contact ids of a deal: «Próximo paso» creates the task on the first. */
  contactIds?: Identifier[];
  /** The source record (mutations send it as previousData). */
  record: object;
}

export type AttentionInbox = Record<AttentionGroupKey, AttentionItem[]>;

export interface AttentionInput {
  /** My open contact tasks (internal and client). */
  tasks: readonly HomeTask[];
  /** My open team tasks. */
  teamTasks: readonly HomeTeamTask[];
  /** Milestones that are not done (team-wide: they are project deadlines). */
  milestones: readonly HomeMilestone[];
  /** Calendar events (any range; only today's are kept). */
  events: readonly HomeEvent[];
  noNextStep: readonly NoNextStepDeal[];
  invoices: readonly HomeInvoice[];
}

const byDateAsc = (left: AttentionItem, right: AttentionItem) =>
  (left.date?.getTime() ?? Infinity) - (right.date?.getTime() ?? Infinity) || 0;

/**
 * The «Necesita tu atención» inbox: one list per group, each sorted by
 * urgency. Overdue has NO look-back limit: a task late by a year is still
 * there (the old «Upcoming deadlines» only looked 14 days back).
 * - overdue: tasks, team tasks and milestones due before today, oldest first;
 * - today: the same sources due today plus today's events not over yet,
 *   timed items by time, then all-day ones;
 * - no_next_step: as given (dealsWithoutNextStep order);
 * - collect: outstanding invoices, overdue first, then by due date.
 */
export const buildAttentionInbox = (
  input: AttentionInput,
  now: Date,
): AttentionInbox => {
  const today = dayKey(now);
  const todayStart = startOfDay(now);
  const lateBy = (date: Date) =>
    Math.max(0, differenceInCalendarDays(todayStart, startOfDay(date)));

  const dated: AttentionItem[] = [
    ...input.tasks
      .filter((task) => !task.done_date && task.due_date)
      .map((task) => {
        const date = toLocalDate(task.due_date);
        return {
          key: `task-${task.id}`,
          source: "task" as const,
          id: task.id,
          title: task.text,
          date,
          timed: !isAllDay(task.due_date),
          daysLate: lateBy(date),
          contactId: task.contact_id,
          taskKind: getTaskKind(task),
          taskType: task.type,
          record: task,
        };
      }),
    ...input.teamTasks
      .filter((task) => task.status !== "done" && task.due_date)
      .map((task) => {
        const date = toLocalDate(task.due_date as string);
        return {
          key: `team_task-${task.id}`,
          source: "team_task" as const,
          id: task.id,
          title: task.title,
          date,
          timed: false,
          daysLate: lateBy(date),
          dealId: task.deal_id,
          priority: task.priority,
          record: task,
        };
      }),
    ...input.milestones
      .filter((milestone) => milestone.status !== "done" && milestone.due_date)
      .map((milestone) => {
        const date = toLocalDate(milestone.due_date as string);
        return {
          key: `milestone-${milestone.id}`,
          source: "milestone" as const,
          id: milestone.id,
          title: milestone.title,
          date,
          timed: false,
          daysLate: lateBy(date),
          dealId: milestone.deal_id,
          amount: milestone.amount ?? undefined,
          record: milestone,
        };
      }),
  ];

  const overdue = dated
    .filter((item) => item.date && dayKey(item.date) < today)
    .sort(byDateAsc);

  const events: AttentionItem[] = input.events
    .filter((event) => dayKey(event.start_at) === today)
    .filter((event) => {
      const allDay = event.all_day || isAllDay(event.start_at);
      if (allDay) return true;
      const end = toLocalDate(event.end_at ?? event.start_at);
      return end.getTime() >= now.getTime();
    })
    .map((event) => ({
      key: `event-${event.id}`,
      source: "event" as const,
      id: event.id,
      title: event.title,
      date: toLocalDate(event.start_at),
      timed: !(event.all_day || isAllDay(event.start_at)),
      daysLate: 0,
      dealId: event.deal_id,
      eventKind: event.kind,
      record: event,
    }));
  const todayItems = [
    ...dated.filter((item) => item.date && dayKey(item.date) === today),
    ...events,
  ].sort(
    (left, right) =>
      Number(right.timed) - Number(left.timed) || byDateAsc(left, right),
  );

  const noNextStep: AttentionItem[] = input.noNextStep.map(
    ({ deal, idleDays, closingPassed }) => {
      const closing = deal.expected_closing_date
        ? toLocalDate(deal.expected_closing_date)
        : null;
      return {
        key: `deal-${deal.id}`,
        source: "deal" as const,
        id: deal.id,
        title: deal.name,
        date: closing,
        timed: false,
        daysLate: closing && closingPassed ? lateBy(closing) : 0,
        amount: amountOf(deal.amount),
        companyId: deal.company_id,
        stage: deal.stage,
        idleDays,
        closingPassed,
        contactIds: [...(deal.contact_ids ?? [])],
        record: deal,
      };
    },
  );

  const collect: AttentionItem[] = input.invoices
    .filter(isOutstanding)
    .map((invoice) => {
      const date = invoice.due_date ? toLocalDate(invoice.due_date) : null;
      const late =
        invoice.status === "overdue" || (date != null && dayKey(date) < today);
      return {
        key: `invoice-${invoice.id}`,
        source: "invoice" as const,
        id: invoice.id,
        title: invoice.number,
        date,
        timed: false,
        daysLate: late && date ? Math.max(1, lateBy(date)) : late ? 1 : 0,
        amount: invoiceGross(invoice),
        companyId: invoice.company_id,
        dealId: invoice.deal_id,
        record: invoice,
      };
    })
    .sort(
      (left, right) =>
        Number(right.daysLate > 0) - Number(left.daysLate > 0) ||
        byDateAsc(left, right),
    );

  return { overdue, today: todayItems, no_next_step: noNextStep, collect };
};

export interface CollectSummary {
  /** Outstanding total, VAT included. */
  total: number;
  overdue: number;
  dueThisWeek: number;
  later: number;
  overdueCount: number;
  dueThisWeekCount: number;
  laterCount: number;
  /** Paid this calendar month (by `paid_at`), VAT included. */
  collectedThisMonth: number;
}

/**
 * «Por cobrar» buckets of the outstanding invoices: overdue (status overdue
 * or due before today), due within the next 7 days, and later (or undated).
 */
export const collectBuckets = (
  invoices: readonly HomeInvoice[],
  now: Date,
): CollectSummary => {
  const today = dayKey(now);
  const weekEnd = dayKey(addDays(startOfDay(now), 6));
  const month = today.slice(0, 7);
  const summary: CollectSummary = {
    total: 0,
    overdue: 0,
    dueThisWeek: 0,
    later: 0,
    overdueCount: 0,
    dueThisWeekCount: 0,
    laterCount: 0,
    collectedThisMonth: 0,
  };
  return invoices.reduce((acc, invoice) => {
    const gross = invoiceGross(invoice);
    if (invoice.status === "paid") {
      return invoice.paid_at && dayKey(invoice.paid_at).slice(0, 7) === month
        ? { ...acc, collectedThisMonth: acc.collectedThisMonth + gross }
        : acc;
    }
    if (!isOutstanding(invoice)) return acc;
    const due = invoice.due_date ? dayKey(invoice.due_date) : null;
    const bucket =
      invoice.status === "overdue" || (due != null && due < today)
        ? "overdue"
        : due != null && due <= weekEnd
          ? "dueThisWeek"
          : "later";
    const countKey = `${bucket}Count` as const;
    return {
      ...acc,
      total: acc.total + gross,
      [bucket]: acc[bucket] + gross,
      [countKey]: acc[countKey] + 1,
    };
  }, summary);
};

export interface TodayBreakdown {
  events: number;
  tasks: number;
  milestones: number;
}

export interface HomeCounters {
  overdue: number;
  /** Days late of the oldest overdue item (0 when none). */
  oldestOverdueDays: number;
  today: number;
  todayBreakdown: TodayBreakdown;
  noNextStep: number;
  noNextStepAmount: number;
  /** Items in the whole inbox (header summary line). */
  attention: number;
}

export const homeCounters = (inbox: AttentionInbox): HomeCounters => ({
  overdue: inbox.overdue.length,
  oldestOverdueDays: inbox.overdue.reduce(
    (max, item) => Math.max(max, item.daysLate),
    0,
  ),
  today: inbox.today.length,
  todayBreakdown: {
    events: inbox.today.filter((item) => item.source === "event").length,
    tasks: inbox.today.filter(
      (item) => item.source === "task" || item.source === "team_task",
    ).length,
    milestones: inbox.today.filter((item) => item.source === "milestone")
      .length,
  },
  noNextStep: inbox.no_next_step.length,
  noNextStepAmount: inbox.no_next_step.reduce(
    (sum, item) => sum + (item.amount ?? 0),
    0,
  ),
  attention: ATTENTION_GROUPS.reduce(
    (sum, group) => sum + inbox[group].length,
    0,
  ),
});

export interface TimelineEntry {
  id: string;
  start: Date;
  end: Date | null;
  allDay: boolean;
  done: boolean;
}

/**
 * «Agenda de hoy»: today's timed entries in order, the all-day ones apart,
 * and where the «now» marker goes (index of the first timed entry that
 * starts after `now`; equals the length when everything is past).
 */
export const todayTimeline = <T extends TimelineEntry>(
  entries: readonly T[],
  now: Date,
): { timed: T[]; allDay: T[]; nowIndex: number } => {
  const today = dayKey(now);
  const ofToday = entries.filter((entry) => dayKey(entry.start) === today);
  const timed = ofToday
    .filter((entry) => !entry.allDay)
    .sort((left, right) => left.start.getTime() - right.start.getTime());
  const allDay = ofToday.filter((entry) => entry.allDay);
  const after = timed.findIndex(
    (entry) => entry.start.getTime() > now.getTime(),
  );
  return { timed, allDay, nowIndex: after < 0 ? timed.length : after };
};

/** Minutes a timed entry with no end is considered to last. */
const DEFAULT_DURATION_MIN = 30;

/**
 * «Siguiente» card: the timed entry in progress or next to start (not done),
 * from today on. Falls back to null when nothing timed is ahead.
 */
export const nextUp = <T extends TimelineEntry>(
  entries: readonly T[],
  now: Date,
): T | null => {
  const today = dayKey(now);
  const endOf = (entry: T) =>
    entry.end ??
    new Date(entry.start.getTime() + DEFAULT_DURATION_MIN * 60 * 1000);
  return (
    entries
      .filter(
        (entry) =>
          !entry.done &&
          !entry.allDay &&
          dayKey(entry.start) >= today &&
          endOf(entry).getTime() > now.getTime(),
      )
      .sort((left, right) => left.start.getTime() - right.start.getTime())[0] ??
    null
  );
};
